import {
  BRAND_VALUE_FILTER_TYPE,
  buildFilterKey,
} from "@faire/retailer-shared/filters/filterKeys";
import { BRAND_ATTRIBUTES } from "@faire/retailer-visitor-shared/consts/makerFilterValues";
import {
  MadeInAustralia,
  MadeInCanada,
  MadeInEurope,
  MadeInEuropeanCountry,
  MadeInUSA,
} from "@faire/retailer-visitor-shared/icons/MadeInCountry/MadeInCountry";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { Earn } from "@faire/web/slate/icons/foundation/Earn";
import { EcoFriendly } from "@faire/web/slate/icons/foundation/illustrations/EcoFriendly";
import { Handmade } from "@faire/web/slate/icons/foundation/illustrations/Handmade";
import { MadeLocally } from "@faire/web/slate/icons/foundation/illustrations/MadeLocally";
import { NotSoldOnAmazon } from "@faire/web/slate/icons/foundation/illustrations/NotSoldOnAmazon";
import { Organic } from "@faire/web/slate/icons/foundation/illustrations/Organic";
import { SmallBatch } from "@faire/web/slate/icons/foundation/illustrations/SmallBatch";
import { SocialGood } from "@faire/web/slate/icons/foundation/illustrations/SocialGood";
import { WomanOwned } from "@faire/web/slate/icons/foundation/illustrations/WomanOwned";
import { SVGIconComponent } from "@faire/web/slate/icons/util";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import type { JSX } from "react";

type BrandValueIcon = (({ ...props }) => JSX.Element) | SVGIconComponent;
interface IBrandValue {
  key: string;
  label: string;
  value: string;
  icon?: BrandValueIcon;
  countryISO?: keyof typeof ISO3166Alpha3;
}

export const BRAND_VALUES = (
  strictLocalize: StrictLocalizeFunction
): { [key: string]: IBrandValue } => ({
  asian_owned: {
    key: BRAND_ATTRIBUTES.ASIAN_OWNED,
    label: strictLocalize({
      defaultMessage: "Asian owned",
      description: {
        text: "Category name for brands that are primarily owned by Asian individuals",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.ASIAN_OWNED
    ),
  },
  black_owned: {
    key: BRAND_ATTRIBUTES.BLACK_OWNED,
    label: strictLocalize({
      defaultMessage: "Black owned",
      description: {
        text: "Category name for brands that are primarily owned by Black individuals",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.BLACK_OWNED
    ),
  },
  /**
   * @deprecated Use GIVES_BACK instead.
   */
  charitable: {
    key: BRAND_ATTRIBUTES.CHARITABLE,
    label: strictLocalize({
      defaultMessage: "Social good",
      description: {
        text: "Category name for brands that are charitable and give back to their community",
      },
    }),
    value: buildFilterKey(BRAND_VALUE_FILTER_TYPE, BRAND_ATTRIBUTES.CHARITABLE),
    icon: SocialGood,
  },
  eco_friendly: {
    key: BRAND_ATTRIBUTES.ECO_FRIENDLY,
    label: strictLocalize({
      defaultMessage: "Eco-friendly",
      description: {
        text: "Category name for brands that are environmentally-friendly",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.ECO_FRIENDLY
    ),
    icon: EcoFriendly,
  },
  gives_back: {
    key: BRAND_ATTRIBUTES.GIVES_BACK,
    label: strictLocalize({
      defaultMessage: "Gives back",
      description: {
        text: "Category name for brands that are charitable and give back to their community",
        img: "https://cdn.faire.com/fastly/3c03cce43d37a45198ddcdbd64f4905f4bf6f606259adbff1c8d70187b963a95.png",
      },
    }),
    value: buildFilterKey(BRAND_VALUE_FILTER_TYPE, BRAND_ATTRIBUTES.GIVES_BACK),
    icon: SocialGood,
  },
  hand_made: {
    key: BRAND_ATTRIBUTES.HAND_MADE,
    label: strictLocalize({
      defaultMessage: "Handmade",
      description: {
        text: "Category name for brands that make their products individually by hand",
      },
    }),
    value: buildFilterKey(BRAND_VALUE_FILTER_TYPE, BRAND_ATTRIBUTES.HAND_MADE),
    icon: Handmade,
  },
  latino_owned: {
    key: BRAND_ATTRIBUTES.LATINO_OWNED,
    label: strictLocalize({
      defaultMessage: "Latino owned",
      description: {
        text: "Category name for brands that are primarily owned by Latino individuals",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.LATINO_OWNED
    ),
  },
  lgbtq_owned: {
    key: BRAND_ATTRIBUTES.LGBTQ_OWNED,
    label: strictLocalize({
      defaultMessage: "LGBTQ owned",
      description: {
        text: "Category name for brands that are primarily owned by LGBTQ+ individuals",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.LGBTQ_OWNED
    ),
  },
  low_minimum: {
    key: BRAND_ATTRIBUTES.LOW_MINIMUM,
    label: strictLocalize({
      defaultMessage: "Low minimum",
      description: {
        text: "Category name for brands that have low minimums",
        img: "https://cdn.faire.com/fastly/876be4439f6c59bdb05f086a679f484b987e1c7bd2e2a7a77c8187b6bbf503ac.png",
      },
    }),
    value: `${BRAND_ATTRIBUTES.LOW_MINIMUM}`,
    icon: Earn,
  },
  made_in_australia: {
    key: BRAND_ATTRIBUTES.MADE_IN_AUSTRALIA,
    label: strictLocalize({
      defaultMessage: "Made in Australia",
      description: {
        text: "Category name for brands that make their products in Australia",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_AUSTRALIA
    ),
    icon: MadeInAustralia,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.AUS as ISO3166Alpha3,
  },
  made_in_austria: {
    key: BRAND_ATTRIBUTES.MADE_IN_AUSTRIA,
    label: strictLocalize({
      defaultMessage: "Made in Austria",
      description: {
        text: "Category name for brands that make their products in Austria",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_AUSTRIA
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.AUT as ISO3166Alpha3,
  },
  made_in_belgium: {
    key: BRAND_ATTRIBUTES.MADE_IN_BELGIUM,
    label: strictLocalize({
      defaultMessage: "Made in Belgium",
      description: {
        text: "Category name for brands that make their products in Belgium",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_BELGIUM
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.BEL as ISO3166Alpha3,
  },
  made_in_canada: {
    key: BRAND_ATTRIBUTES.MADE_IN_CANADA,
    label: strictLocalize({
      defaultMessage: "Made in Canada",
      description: {
        text: "Category name for brands that make their products in Canada",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_CANADA
    ),
    icon: MadeInCanada,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN as ISO3166Alpha3,
  },
  made_in_denmark: {
    key: BRAND_ATTRIBUTES.MADE_IN_DENMARK,
    label: strictLocalize({
      defaultMessage: "Made in Denmark",
      description: {
        text: "Category name for brands that make their products in Denmark",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_DENMARK
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.DNK as ISO3166Alpha3,
  },
  made_in_europe: {
    key: BRAND_ATTRIBUTES.MADE_IN_EUROPE,
    label: strictLocalize({
      defaultMessage: "Made in Europe",
      description: {
        text: "Category name for brands that make their products in Europe",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_EUROPE
    ),
    icon: MadeInEurope,
  },
  made_in_finland: {
    key: BRAND_ATTRIBUTES.MADE_IN_FINLAND,
    label: strictLocalize({
      defaultMessage: "Made in Finland",
      description: {
        text: "Category name for brands that make their products in Finland",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_FINLAND
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.FIN as ISO3166Alpha3,
  },
  made_in_france: {
    key: BRAND_ATTRIBUTES.MADE_IN_FRANCE,
    label: strictLocalize({
      defaultMessage: "Made in France",
      description: {
        text: "Category name for brands that make their products in the France",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_FRANCE
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.FRA as ISO3166Alpha3,
  },
  made_in_germany: {
    key: BRAND_ATTRIBUTES.MADE_IN_GERMANY,
    label: strictLocalize({
      defaultMessage: "Made in Germany",
      description: {
        text: "Category name for brands that make their products in the Germany",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_GERMANY
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.DEU as ISO3166Alpha3,
  },
  made_in_ireland: {
    key: BRAND_ATTRIBUTES.MADE_IN_IRELAND,
    label: strictLocalize({
      defaultMessage: "Made in Ireland",
      description: {
        text: "Category name for brands that make their products in Ireland",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_IRELAND
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.IRL as ISO3166Alpha3,
  },
  made_in_italy: {
    key: BRAND_ATTRIBUTES.MADE_IN_ITALY,
    label: strictLocalize({
      defaultMessage: "Made in Italy",
      description: {
        text: "Category name for brands that make their products in Italy",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_ITALY
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.ITA as ISO3166Alpha3,
  },
  made_in_luxembourg: {
    key: BRAND_ATTRIBUTES.MADE_IN_LUXEMBOURG,
    label: strictLocalize({
      defaultMessage: "Made in Luxembourg",
      description: {
        text: "Category name for brands that make their products in Luxembourg",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_LUXEMBOURG
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.LUX as ISO3166Alpha3,
  },
  made_in_netherlands: {
    key: BRAND_ATTRIBUTES.MADE_IN_NETHERLANDS,
    label: strictLocalize({
      defaultMessage: "Made in Netherlands",
      description: {
        text: "Category name for brands that make their products in the Netherlands",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_NETHERLANDS
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.NLD as ISO3166Alpha3,
  },
  made_in_norway: {
    key: BRAND_ATTRIBUTES.MADE_IN_NORWAY,
    label: strictLocalize({
      defaultMessage: "Made in Norway",
      description: {
        text: "Category name for brands that make their products in Norway",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_NORWAY
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.NOR as ISO3166Alpha3,
  },
  made_in_portugal: {
    key: BRAND_ATTRIBUTES.MADE_IN_PORTUGAL,
    label: strictLocalize({
      defaultMessage: "Made in Portugal",
      description: {
        text: "Category name for brands that make their products in Portugal",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_PORTUGAL
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.PRT as ISO3166Alpha3,
  },
  made_in_spain: {
    key: BRAND_ATTRIBUTES.MADE_IN_SPAIN,
    label: strictLocalize({
      defaultMessage: "Made in Spain",
      description: {
        text: "Category name for brands that make their products in Spain",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_SPAIN
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.ESP as ISO3166Alpha3,
  },
  made_in_sweden: {
    key: BRAND_ATTRIBUTES.MADE_IN_SWEDEN,
    label: strictLocalize({
      defaultMessage: "Made in Sweden",
      description: {
        text: "Category name for brands that make their products in Sweden",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_SWEDEN
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.SWE as ISO3166Alpha3,
  },
  made_in_switzerland: {
    key: BRAND_ATTRIBUTES.MADE_IN_SWITZERLAND,
    label: strictLocalize({
      defaultMessage: "Made in Switzerland",
      description: {
        text: "Category name for brands that make their products in Switzerland",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_SWITZERLAND
    ),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.CHE as ISO3166Alpha3,
  },
  made_in_uk: {
    key: BRAND_ATTRIBUTES.MADE_IN_UK,
    label: strictLocalize({
      defaultMessage: "Made in UK",
      description: {
        text: "Category name for brands that make their products in the UK",
      },
    }),
    value: buildFilterKey(BRAND_VALUE_FILTER_TYPE, BRAND_ATTRIBUTES.MADE_IN_UK),
    icon: MadeInEuropeanCountry,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.GBR as ISO3166Alpha3,
  },
  made_in_usa: {
    key: BRAND_ATTRIBUTES.MADE_IN_USA,
    label: strictLocalize({
      defaultMessage: "Made in USA",
      description: {
        text: "Category name for brands that make their products in the USA",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.MADE_IN_USA
    ),
    icon: MadeInUSA,
    countryISO: THREE_LETTER_PRIMARY_COUNTRY_CODES.USA as ISO3166Alpha3,
  },
  not_sold_on_amazon: {
    key: BRAND_ATTRIBUTES.NOT_SOLD_ON_AMAZON,
    label: strictLocalize({
      defaultMessage: "Not on Amazon",
      description: {
        text: "Category name for brands that don't sell their products on Amazon",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.NOT_SOLD_ON_AMAZON
    ),
    icon: NotSoldOnAmazon,
  },
  organic: {
    key: BRAND_ATTRIBUTES.ORGANIC,
    label: strictLocalize({
      defaultMessage: "Organic",
      description: {
        text: "Category name for brands that make their products from natural ingredients",
      },
    }),
    value: buildFilterKey(BRAND_VALUE_FILTER_TYPE, BRAND_ATTRIBUTES.ORGANIC),
    icon: Organic,
  },
  /**
   * @deprecated no direct replacement, avoid using.
   */
  small_batch: {
    key: BRAND_ATTRIBUTES.SMALL_BATCH,
    label: strictLocalize({
      defaultMessage: "Small batch",
      description: {
        text: "Category name for brands that make their products in limited quantities",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.SMALL_BATCH
    ),
    icon: SmallBatch,
  },
  shop_local: {
    key: BRAND_ATTRIBUTES.SHOP_LOCAL,
    label: strictLocalize({
      defaultMessage: "Shop local",
      description: {
        text: "Category name for brands based in the same geographical region as the retailer",
      },
    }),
    value: buildFilterKey(BRAND_VALUE_FILTER_TYPE, BRAND_ATTRIBUTES.SHOP_LOCAL),
    icon: MadeLocally,
  },
  women_owned: {
    key: BRAND_ATTRIBUTES.WOMEN_OWNED,
    label: strictLocalize({
      defaultMessage: "Women owned",
      description: {
        text: "Category name for brands that are primarily owned by women",
      },
    }),
    value: buildFilterKey(
      BRAND_VALUE_FILTER_TYPE,
      BRAND_ATTRIBUTES.WOMEN_OWNED
    ),
    icon: WomanOwned,
  },
});

const BRAND_VALUES_ICON_MAP = (strictLocalize: StrictLocalizeFunction) =>
  Object.values(BRAND_VALUES(strictLocalize)).reduce<
    Record<string, BrandValueIcon>
  >((acc, entry) => {
    if (entry.icon) {
      acc[entry.value] = entry.icon;
    }
    return acc;
  }, {});

export const getBrandValueIcon = (
  filterKey: string,
  strictLocalize: StrictLocalizeFunction
) => {
  const isLowMinimumFilter = filterKey.startsWith("maker_minimum:");
  const BrandValueIcon =
    BRAND_VALUES_ICON_MAP(strictLocalize)[
      isLowMinimumFilter ? BRAND_ATTRIBUTES.LOW_MINIMUM : filterKey
    ];
  return BrandValueIcon;
};
